/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
  DatePicker,
  TreeSelect,
  Input,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  DownloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import Dragger from 'components/Dragger';
import { useSelector } from 'react-redux';
import ExcelJS from 'exceljs';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getAreas, getEstadosGlobales } from 'api/catalogos/index';
import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_INACTIVO,
  ESTATUS_AUTORIZADO,
} from 'utils/constants';
import {
  formatTreeSelectData,
  importarExcelGuardiasExcepciones,
  ModelInfoConfigGuardVacaExcepcion,
  normalizeDataAreas,
} from 'api/configuraciones/ConfigurarGuardiasVacacionales/ConfigurarGuardiasVacacionalesExcepciones';
import moment from 'moment';
import { getPuestos } from 'api/catalogos/puestos';
import { getEmpleadosFiltered } from 'api/estructuraOrganizacional/empleados';
import { getPlazasvistaOptimized } from 'api/catalogos/plazas';
import { getAdministracionDePlazasvistaOptimized } from 'api/catalogos/admnistracionDePlazas';

const modelPermissionName = 'cargadereportedetalle';

export const configurarGuardiasVacacionalesExcepcionesPermission = {
  permissionModel: modelPermissionName,
  catalogsModels: [],
  tabsModels: [],
};

const ConfigurarGuardiasVacacionalesExcepciones = ({ permission, idFather }) => {
  const [form] = Form.useForm();
  const [formCarga] = Form.useForm();
  const areaSelected = Form.useWatch('areas', form);
  const puestoSelected = Form.useWatch('puesto', form);
  const updateFieldName = 'aplica_todos_puestos';

  const nombreEntidad = useSelector(({ auth }) => auth.entidad?.nombre || '');

  const [isAdding, setAdding] = useState(true);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedData, setSelectedData] = useState({});
  const [open, setVisible] = useState(false);
  const [estados, setEstados] = useState([]);
  const [visibleConfirmInactivo, setVisibleConfirmInactivo] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [disableFields, setDisableFields] = useState(false);
  const [visibleModalCarga, setVisibleModalCarga] = useState(false);

  const [areas, setAreas] = useState([]);
  const [rawAreas, setRawAreas] = useState([]);
  const [todasLasPlazas, setTodasLasPlazas] = useState([]);
  const [todosLosPuestos, setTodosLosPuestos] = useState([]);
  const [todosLosEmpleados, setTodosLosEmpleados] = useState([]);
  // Mapa plaza_id → empleado_id construido desde asignaciones
  const [plazaEmpleadoMap, setPlazaEmpleadoMap] = useState({});

  const [puestosFiltrados, setPuestosFiltrados] = useState([]);
  const [empleadosFiltrados, setEmpleadosFiltrados] = useState([]);

  const registroName = `${selectedData?.areas?.clave_y_nombre}`;
  const {
    cliente,
    nameModel,
    nameModelSingular,
    formatter,
  } = ModelInfoConfigGuardVacaExcepcion;

  const formInstances = [form];

  const fetchData = async () => {
    setLoading(true);
    const _data = await cliente.obtenerListaDeRegistros(
      { configurar_guardias_vacacionales_general: idFather[0] },
    );
    setData(_data);
    setLoading(false);
  };

  // ─── Carga inicial: áreas, puestos, empleados, plazas, estados y registros ───
  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      // eslint-disable-next-line max-len
      const [_areas, _puestos, _empleados, _plazas, _asignaciones, _estados, _data] = await Promise.all([
        getAreas({ estados_globales: ESTATUS_AUTORIZADO }),
        getPuestos({ estados_globales: ESTATUS_AUTORIZADO }),
        getEmpleadosFiltered(),
        getPlazasvistaOptimized(),
        getAdministracionDePlazasvistaOptimized(),
        getEstadosGlobales(),
        cliente.obtenerListaDeRegistros(
          { configurar_guardias_vacacionales_general: idFather[0] },
        ),
      ]);
      if (mounted) {
        const areasFiltradas = _areas.filter((e) => e.directorio_area_historico === null);
        setRawAreas(areasFiltradas);
        setAreas(formatTreeSelectData(normalizeDataAreas(areasFiltradas)));
        setTodosLosPuestos(Array.isArray(_puestos) ? _puestos : []);
        setTodosLosEmpleados(Array.isArray(_empleados) ? _empleados : []);
        setTodasLasPlazas(Array.isArray(_plazas) ? _plazas : []);

        // Construir mapa plaza_id → empleado_id desde las asignaciones
        const asigs = Array.isArray(_asignaciones) ? _asignaciones : [];
        const mapa = {};
        asigs.forEach((a) => {
          if (a.plaza_id && a.empleado_id) {
            mapa[String(a.plaza_id)] = a.empleado_id;
          }
        });
        setPlazaEmpleadoMap(mapa);

        setEstados(_estados);
        setData(_data);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  // Filtrado local: Área → Puestos → Empleados
  useEffect(() => {
    if (!areaSelected && !puestoSelected?.length) {
      setPuestosFiltrados([]);
      setEmpleadosFiltrados(todosLosEmpleados);
      return;
    }

    const buscarNodoPorValue = (nodos, valueBuscado) => {
      const directo = nodos.find(
        (n) => String(n.value) === String(valueBuscado)
          || String(n.key) === String(valueBuscado),
      );
      if (directo) return directo;
      return nodos.reduce((acc, n) => {
        if (acc) return acc;
        if (n.children?.length) return buscarNodoPorValue(n.children, valueBuscado);
        return null;
      }, null);
    };

    const nodoAreaReal = areaSelected ? buscarNodoPorValue(areas, areaSelected) : null;
    const claveABuscar = nodoAreaReal
      ? (nodoAreaReal.clave || nodoAreaReal.title || nodoAreaReal.label)
      : '';
    const idRealABuscar = nodoAreaReal
      ? (nodoAreaReal.id || nodoAreaReal.directorio_area_id)
      : null;

    let plazasFiltradasPorArea = todasLasPlazas;
    if (areaSelected) {
      plazasFiltradasPorArea = todasLasPlazas.filter((plaza) => {
        const areaIdStr = String(plaza.directorio_area_id);
        const areaClave = String(plaza.directorio_area_clave).toLowerCase();
        return (idRealABuscar && areaIdStr === String(idRealABuscar))
          || (claveABuscar && String(claveABuscar).toLowerCase().includes(areaClave))
          || areaIdStr === String(areaSelected);
      });

      const puestoIds = new Set(
        plazasFiltradasPorArea.map((p) => String(p.puesto_id)).filter(Boolean),
      );
      setPuestosFiltrados(
        todosLosPuestos.filter((p) => puestoIds.has(String(p.id))),
      );
    } else {
      setPuestosFiltrados(todosLosPuestos);
    }

    let plazasFinalesParaEmpleados = plazasFiltradasPorArea;
    if (puestoSelected?.length) {
      const puestosSet = new Set(puestoSelected.map(String));
      plazasFinalesParaEmpleados = plazasFiltradasPorArea.filter(
        (plaza) => puestosSet.has(String(plaza.puesto_id)),
      );
    }

    // Obtener empleado_id cruzando plaza.id con el mapa de asignaciones
    const empIds = new Set(
      plazasFinalesParaEmpleados
        .map((plaza) => plazaEmpleadoMap[String(plaza.id)])
        .filter(Boolean)
        .map(String),
    );

    setEmpleadosFiltrados(
      empIds.size > 0
        ? todosLosEmpleados.filter((emp) => empIds.has(String(emp.id)))
        : [],
    );
  // eslint-disable-next-line max-len
  }, [areaSelected, puestoSelected, areas, todasLasPlazas, todosLosPuestos, todosLosEmpleados, plazaEmpleadoMap]);
  // ─── Descargar plantilla Excel ───────────────────────────────────────────
  const onClickDescargarPlantilla = async () => {
    try {
      setLoading(true);

      // Usar los datos ya cargados en el estado del componente (fetchAll los carga al montar)
      const _areas = rawAreas;
      const _puestos = todosLosPuestos;
      const _empleados = todosLosEmpleados;

      const AZUL_OSCURO = 'FF003366';
      const AZUL_TITULO = 'FF1F3864';
      const BLANCO = 'FFFFFFFF';
      const GRIS = 'FFD9D9D9';

      const workbook = new ExcelJS.Workbook();
      const hoy = moment();

      // ── Hoja principal "Excepciones" (primera) ────────────────────────────
      const ws = workbook.addWorksheet('Excepciones');
      ws.getColumn('A').width = 50;
      ws.getColumn('B').width = 45;
      ws.getColumn('C').width = 40;

      // Fila 1: nombre entidad
      ws.getRow(1).height = 40;
      ws.mergeCells('A1:C1');
      const celdaEntidad = ws.getCell('A1');
      celdaEntidad.value = nombreEntidad.toUpperCase();
      celdaEntidad.font = {
        name: 'Calibri', size: 20, bold: true, color: { argb: AZUL_TITULO },
      };
      celdaEntidad.alignment = { horizontal: 'left', vertical: 'middle' };

      // Fila 2: línea decorativa
      ws.getRow(2).height = 6;
      ws.mergeCells('A2:C2');
      ws.getCell('A2').fill = {
        type: 'pattern', pattern: 'solid', fgColor: { argb: AZUL_OSCURO },
      };

      // Fila 3: vacía
      ws.getRow(3).height = 8;

      // Fila 4: PLANTILLA DE CARGA PARA
      ws.getRow(4).height = 20;
      ws.mergeCells('A4:B4');
      const etiq4 = ws.getCell('A4');
      etiq4.value = 'PLANTILLA DE CARGA PARA:';
      etiq4.font = { name: 'Calibri', size: 11, bold: true };
      etiq4.alignment = { horizontal: 'left', vertical: 'middle' };
      const val4 = ws.getCell('C4');
      val4.value = 'Excepciones de Guardias Vacacionales';
      val4.font = { name: 'Calibri', size: 11 };
      val4.alignment = { horizontal: 'left', vertical: 'middle' };

      // Fila 5: FECHA
      ws.getRow(5).height = 20;
      ws.mergeCells('A5:B5');
      const etiq5 = ws.getCell('A5');
      etiq5.value = 'FECHA';
      etiq5.font = { name: 'Calibri', size: 11, bold: true };
      etiq5.alignment = { horizontal: 'left', vertical: 'middle' };
      const val5 = ws.getCell('C5');
      val5.value = `${hoy.month() + 1}/${hoy.date()}/${hoy.year()}`;
      val5.font = { name: 'Calibri', size: 11 };
      val5.alignment = { horizontal: 'left', vertical: 'middle' };

      // Fila 6: vacía
      ws.getRow(6).height = 8;

      // Fila 7: encabezados (3 columnas, sin Vigencia Final)
      ws.getRow(7).height = 22;
      [
        ['A7', '*Área (Clave)'],
        ['B7', 'Puestos (Claves separadas por coma)'],
        ['C7', 'Empleados (Núm. separados por coma)'],
      ].forEach(([addr, titulo]) => {
        const cell = ws.getCell(addr);
        cell.value = titulo;
        cell.font = {
          name: 'Calibri', size: 11, bold: true, color: { argb: BLANCO },
        };
        cell.fill = {
          type: 'pattern', pattern: 'solid', fgColor: { argb: AZUL_OSCURO },
        };
        cell.alignment = {
          horizontal: 'center', vertical: 'middle', wrapText: true,
        };
        cell.border = {
          left: { style: 'thin', color: { argb: GRIS } },
          right: { style: 'thin', color: { argb: GRIS } },
          top: { style: 'thin', color: { argb: GRIS } },
          bottom: { style: 'thin', color: { argb: GRIS } },
        };
      });

      // ── Hoja oculta "Catalogos" para dropdowns ────────────────────────────
      const catSheet = workbook.addWorksheet('Catalogos', { state: 'hidden' });
      _areas.forEach((a, i) => {
        catSheet.getCell(i + 1, 1).value = a.clave && a.nombre
          ? `${a.clave} - ${a.nombre}`
          : (a.clave || '');
      });
      _puestos.forEach((p, i) => {
        catSheet.getCell(i + 1, 2).value = p.clave_y_descripcion || p.clave || '';
      });

      // Dropdown Área (col A)
      if (_areas.length > 0) {
        ws.dataValidations.add('A8:A10000', {
          type: 'list',
          allowBlank: true,
          formulae: [`Catalogos!$A$1:$A$${_areas.length}`],
          showErrorMessage: true,
          errorTitle: 'Valor inválido',
          error: 'Seleccione un área de la lista desplegable.',
          showInputMessage: true,
          promptTitle: 'Área',
          prompt: 'Haga clic en la flecha para elegir.',
        });
      }

      // Dropdown Puestos (col B)
      if (_puestos.length > 0) {
        ws.dataValidations.add('B8:B10000', {
          type: 'list',
          allowBlank: true,
          formulae: [`Catalogos!$B$1:$B$${_puestos.length}`],
          showErrorMessage: true,
          errorTitle: 'Valor inválido',
          error: 'Seleccione un puesto de la lista desplegable.',
          showInputMessage: true,
          promptTitle: 'Puesto',
          prompt: 'Haga clic en la flecha para elegir.',
        });
      }

      // ── Hojas de referencia visibles ──────────────────────────────────────
      if (_areas.length > 0) {
        const wsAreas = workbook.addWorksheet('Catálogo Áreas');
        wsAreas.getColumn('A').width = 20;
        wsAreas.getColumn('B').width = 70;
        ['A1', 'B1'].forEach((addr, idx) => {
          const cell = wsAreas.getCell(addr);
          cell.value = ['Clave', 'Nombre'][idx];
          cell.font = { bold: true, color: { argb: BLANCO } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: AZUL_OSCURO } };
          cell.alignment = { horizontal: 'center' };
        });
        _areas.forEach((a, i) => {
          wsAreas.getCell(i + 2, 1).value = a.clave || '';
          wsAreas.getCell(i + 2, 2).value = a.nombre || '';
        });
      }

      if (_puestos.length > 0) {
        const wsPuestos = workbook.addWorksheet('Catálogo Puestos');
        wsPuestos.getColumn('A').width = 20;
        wsPuestos.getColumn('B').width = 70;
        ['A1', 'B1'].forEach((addr, idx) => {
          const cell = wsPuestos.getCell(addr);
          cell.value = ['Clave', 'Descripción'][idx];
          cell.font = { bold: true, color: { argb: BLANCO } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: AZUL_OSCURO } };
          cell.alignment = { horizontal: 'center' };
        });
        _puestos.forEach((p, i) => {
          wsPuestos.getCell(i + 2, 1).value = p.clave || '';
          wsPuestos.getCell(i + 2, 2).value = p.clave_y_descripcion || '';
        });
      }

      if (_empleados.length > 0) {
        const wsEmpleados = workbook.addWorksheet('Catálogo Empleados');
        wsEmpleados.getColumn('A').width = 20;
        wsEmpleados.getColumn('B').width = 70;
        ['A1', 'B1'].forEach((addr, idx) => {
          const cell = wsEmpleados.getCell(addr);
          cell.value = ['Núm. Empleado', 'Nombre'][idx];
          cell.font = { bold: true, color: { argb: BLANCO } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: AZUL_OSCURO } };
          cell.alignment = { horizontal: 'center' };
        });
        _empleados.forEach((e, i) => {
          wsEmpleados.getCell(i + 2, 1).value = e.numero_de_empleado || '';
          wsEmpleados.getCell(i + 2, 2).value = e.numero_y_nombre_completo || '';
        });
      }

      // ── Generar y descargar ───────────────────────────────────────────────
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Layout_Excepciones_Guardias_Vacacionales.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('Plantilla descargada correctamente');
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  // ─── Cargar Excel ─────────────────────────────────────────────────────────
  const onUpload = async () => {
    try {
      await formCarga.validateFields();
      const { file } = formCarga.getFieldsValue();
      if (file) {
        setLoading(true);
        const [generalId] = idFather;
        const resultado = await importarExcelGuardiasExcepciones(file.file, generalId);
        if (resultado) {
          const { importados, errores } = resultado;
          if (errores && errores.length > 0) {
            errores.forEach((e) => message.error(e, 6));
          }
          if (importados > 0) {
            message.success(`Se importaron ${importados} excepción(es) correctamente.`);
          }
          if (importados === 0 && (!errores || errores.length === 0)) {
            message.info('No se importó ningún registro.');
          }
          setVisibleModalCarga(false);
          formCarga.resetFields();
          await fetchData();
        }
      }
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const valuesForm = form.getFieldsValue();
      const [key] = selectedRowKeys;
      const values = formatter.formatRecordForSend(valuesForm);
      const saved = await (
        key
          ? cliente.actualizarRegistro(key, values)
          : cliente.crearRegistro(values)
      );
      if (saved) {
        if (_continue) handleOnRowClick(saved);
        else onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const onClickAdd = () => {
    setAdding(true);
    form.resetFields();
    onCancel();
    setVisible(true);
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    setAdding(false);
    const [key] = selectedRowKeys;
    const match = responseData || data?.find((item) => item.id === key);
    const _registro = await cliente.obtenerUnRegistro(match.id);
    setTimeout(() => {
      if (_registro) {
        const formValues = formatter.formatRecordForRead(_registro);
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(formValues));
          setDisableFields(_registro?.estados_globales?.id !== ESTATUS_ACTIVO);
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onClickDelete = () => { setVisibleAlert(true); };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await cliente.borrarRegistro(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, `No se puede autorizar ${nameModelSingular} ya autorizado`);
        return;
      }
      const saved = await cliente.autorizarRegistro(key);
      if (saved) { await fetchData(); onCancel(); }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const rowSelection = { selectedRowKeys, type: 'radio' };

  const columns = [
    {
      titleText: 'Área',
      dataIndex: 'areas',
      key: 'areas',
      width: 100,
      render: (_, record) => record?.areas?.clave_y_nombre || '',
    },
    {
      titleText: 'Puesto',
      dataIndex: 'puesto',
      key: 'puesto',
      width: 200,
      render: (_, record) => record?.puesto?.map((e) => e?.clave_y_descripcion).join(', ') || '',
    },
    {
      titleText: 'Empleado',
      dataIndex: 'empleado',
      key: 'empleado',
      width: 100,
      render: (_, record) => record?.empleado?.numero_y_nombre_completo || '',
    },
    {
      titleText: 'Fecha de Registro',
      dataIndex: 'fecha_registro',
      key: 'fecha_registro',
      width: 150,
      render: (val) => moment(val).format('DD/MM/YYYY'),
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigencia_final',
      key: 'vigencia_final',
      width: 150,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 180,
      render: (_, record) => record?.estados_globales?.descripcion || '',
    },
  ];

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          customActions={[
            {
              onClick: () => setVisibleAutorizar(true),
              permission: { model: 'cargadereportedetalle', action: 'autorizar' },
              icon: <CheckCircleOutlined />,
              text: 'Autorizar',
              disabledBySelection: true,
              disabled: selectedData?.estados_globales?.id !== ESTATUS_ACTIVO,
              className: 'success',
            },
            {
              onClick: onClickDescargarPlantilla,
              icon: <DownloadOutlined />,
              text: 'Descargar Plantilla',
              disabledBySelection: false,
            },
            {
              onClick: () => setVisibleModalCarga(true),
              icon: <UploadOutlined />,
              text: 'Cargar Excel',
              disabledBySelection: false,
            },
          ]}
          permission={permission}
          allowExportParam={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} ${nameModel} `}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstErrorDatePicker
            name="general"
            initialValues={{ estados_globales: ESTATUS_ACTIVO }}
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item name="areas" label="Área" hasFeedback>
                <TreeSelect
                  showSearch
                  treeNodeFilterProp="nombre"
                  treeData={areas}
                  disabled={disableFields}
                  onChange={() => {
                    form.setFieldsValue({ puesto: undefined, empleado: undefined });
                  }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name={updateFieldName}
                label="Aplica para Todos los Puestos"
                initialValue={false}
                hasFeedback
              >
                <Select trueFalse disabled={disableFields} />
              </Form.Item>
            </Col>

            <Form.Item shouldUpdate noStyle>
              {({ getFieldValue }) => {
                const aplicaTodos = getFieldValue(updateFieldName);
                return !aplicaTodos ? (
                  <Col xs={24} sm={24} md={24}>
                    <Form.Item name="puesto" label="Puesto" hasFeedback>
                      <Select
                        mode="multiple"
                        dataSource={puestosFiltrados}
                        keyProp="id"
                        labelProp="clave_y_descripcion"
                        allowClear
                        disabled={disableFields}
                        onChange={() => {
                          form.setFieldsValue({ empleado: undefined });
                        }}
                      />
                    </Form.Item>
                  </Col>
                ) : null;
              }}
            </Form.Item>

            <Col xs={24} sm={24} md={24}>
              <Form.Item name="empleado" label="Empleado" hasFeedback>
                <Select
                  mode="multiple"
                  dataSource={empleadosFiltrados}
                  labelProp="numero_y_nombre_completo"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="fecha_registro"
                label="Fecha de Registro"
                initialValue={moment()}
                hasFeedback
              >
                <DatePicker allowClear format="DD/MM/YYYY" placeholder="" disabled />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item name="vigencia_final" label="Vigencia Final" hasFeedback>
                <DatePicker
                  allowClear
                  format="DD-MM-YYYY"
                  placeholder=""
                  disabled={isAdding || selectedData?.estados_globales?.id === ESTATUS_INACTIVO}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item name="estados_globales" label="Estatus" hasFeedback>
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={estados}
                  activeInactive={selectedData?.estados_globales === ESTATUS_ACTIVO}
                  disabled={selectedRowKeys.length <= 0 || disableFields}
                  onChange={(val) => setVisibleConfirmInactivo(val === ESTATUS_INACTIVO)}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="configurar_guardias_vacacionales_general"
                hasFeedback
                hidden
                initialValue={idFather[0]}
              >
                <Input hidden />
              </Form.Item>
            </Col>

            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>

        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`${nameModelSingular} ${registroName}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle={`Autorizar ${nameModelSingular} ${registroName}`}
          open={visibleAutorizar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => setVisibleConfirmInactivo(false)}
          ConfirmInactive
          onCancel={() => {
            form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
            setVisibleConfirmInactivo(false);
          }}
          open={visibleConfirmInactivo}
          contentTitle={`Inactivar ${nameModelSingular} ${registroName}`}
          loading={loading}
        />

        {/* Modal: Cargar Excel */}
        <Modal
          open={visibleModalCarga}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label="Carga Masiva de Excepciones de Guardias Vacacionales"
              onFinish={onUpload}
              onCancel={() => {
                setVisibleModalCarga(false);
                formCarga.resetFields();
              }}
              loading={loading}
            />
          )}
          onCancel={() => { setVisibleModalCarga(false); formCarga.resetFields(); }}
        >
          <Spin tip="Cargando..." spinning={loading}>
            <Form
              form={formCarga}
              layout="vertical"
              name="cargaGuardiasExcepciones"
            >
              <Dragger
                label="Seleccione el archivo Excel (.xlsx)"
                limitMB={20}
                rules={[{ required: true, message: 'El archivo es obligatorio' }]}
                name="file"
                allowedExtensions={['xlsx']}
              />
            </Form>
          </Spin>
        </Modal>
      </Spin>
    </Row>
  );
};

export default ConfigurarGuardiasVacacionalesExcepciones;
